import PropTypes from 'prop-types';
import React, { useCallback } from 'react';
import { useHits } from 'react-instantsearch';

import { analyticsEvents, trackConversion } from '@general/helpers/algolia.helpers';
import { SearchHitsGlobalForType, SearchState } from '@search';
import useIcscTracking from '@tracking/useIcscTracking';
import { useGetEventName, useHandleLogConversion, useShouldLogConversion } from './hooks';

const SearchResults = ({
    cardComponent,
    index,
    listItemComponent,
    parentView, // TODO: Can this be stored in context instead of passed down 2-3 levels?
    query,
    parentSubview,
    transformFunc,
    typePlural,
    resultsPerRow = 3,
    NoResultsText,
    onHitClick,
}) => {
    const { hits, sendEvent } = useHits();
    const { trackEvent } = useIcscTracking();
    const getEventName = useGetEventName(index, parentView, parentSubview);
    const shouldLogConversion = useShouldLogConversion(index, parentView, parentSubview);
    const handleLogConversion = useHandleLogConversion(shouldLogConversion, trackEvent, index, getEventName);

    const handleGenericClick = useCallback(
        ({ hit }) => {
            trackEvent({
                click: {
                    index: `${process.env.ALGOLIA_ENV_PREFIX}_${index}`,
                    hit,
                    target: `click_search_result_${index}`,
                    value: 'Click Search Result',
                },
            });

            handleLogConversion(hit);

            if (onHitClick) {
                onHitClick({ hit });
            }

            sendEvent('click', hit, analyticsEvents.HIT_CLICKED);
        },
        [handleLogConversion, index, onHitClick, sendEvent, trackEvent]
    );

    const handleAdded = useCallback(
        ({ hit }) => {
            handleLogConversion(hit);
            trackConversion({
                eventName: analyticsEvents.FAVORITE_CLICKED,
                index,
                objectID: hit.objectID,
                queryID: hit.__queryID,
            });
        },
        [handleLogConversion, index]
    );

    if (hits.length === 0) {
        return <SearchState NoResultsText={NoResultsText} />;
    }

    return (
        <SearchHitsGlobalForType
            CardComponent={cardComponent}
            hits={hits}
            index={index}
            ListItemComponent={listItemComponent}
            isLoggedIn
            onClick={handleGenericClick}
            query={query}
            added={handleAdded}
            typePlural={typePlural}
            transformFunc={transformFunc}
            resultsPerRow={resultsPerRow}
            onMessageSent={({ hit }) => {
                handleLogConversion(hit);
                trackConversion({
                    eventName: analyticsEvents.MEMBER_MESSAGE_SENT,
                    index,
                    objectID: hit.objectID,
                    queryID: hit.__queryID,
                });
            }}
        />
    );
};

SearchResults.propTypes = {
    cardComponent: PropTypes.any.isRequired,
    index: PropTypes.string.isRequired,
    listItemComponent: PropTypes.any.isRequired,
    NoResultsText: PropTypes.elementType,
    parentView: PropTypes.string,
    parentSubview: PropTypes.string,
    query: PropTypes.object,
    transformFunc: PropTypes.func,
    typePlural: PropTypes.string,
    resultsPerRow: PropTypes.number,
    onHitClick: PropTypes.func,
};

export default SearchResults;
